/**
 * Auth journal — why did the page leave where the person was working?
 *
 * 2026-10-21: a person working in CLU NG was thrown back to the home screen
 * several times a day. The backend rejected nothing (no 401/403 before any
 * jump), no frontend deploy matched, and the console lost the evidence on the
 * reload itself. Nine code paths can redirect to the login or reload the page;
 * from outside none of them can be told apart.
 *
 * Every such path records one entry here. Entries live in localStorage, so
 * they survive the reload they describe. Read them in the console with
 * `__gridAuthJournal`.
 *
 * Switched by the `auth.journal.enabled` setting. Its last known value is kept
 * in localStorage so a recording during a reload — before any request can be
 * made — still knows whether to record. Unknown reads as off.
 *
 * What an entry holds: when, which path asked, the reason it gave, the page
 * (pathname only), whether the tab was visible and whether a signed-in user
 * was known at that instant, plus a few stack frames. Never a token, a key, an
 * email or a query string. Recording never throws.
 */

const STORAGE_KEY = 'grid:auth_journal';
const SWITCH_KEY = 'grid:auth_journal_enabled';
export const AUTH_JOURNAL_SETTING = 'auth.journal.enabled';
const MAX_ENTRIES = 40;
const MAX_STACK_FRAMES = 6;
const DUPLICATE_WINDOW_MS = 2000;

export interface AuthJournalEntry {
  at: string;
  source: string;
  reason?: string;
  path: string;
  visible: boolean;
  user?: boolean;
  role?: boolean;
  detail?: Record<string, string | number | boolean | null>;
  stack?: string[];
}

function read(): AuthJournalEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function frames(): string[] {
  try {
    const lines = (new Error().stack || '').split('\n').map((l) => l.trim()).filter(Boolean);
    return lines
      .filter((l) => !l.includes('authJournal'))
      .slice(0, MAX_STACK_FRAMES)
      .map((l) => l.replace(/[?#][^\s):]*/g, ''));
  } catch {
    return [];
  }
}

/** A path for the journal: no query or fragment, and ids, UUIDs, emails and
 * token-shaped segments (20+ url-safe characters with a digit: /i/:token,
 * /pub/artifact/:token, /m/verify/face/:token) collapsed to `:id`. */
export function redactPath(path: unknown): string {
  return String(path || '')
    .split(/[?#]/)[0]
    .split('/')
    .map((seg) => (
      /^\d+$/.test(seg)
      || /^[0-9a-f-]{32,36}$/i.test(seg)
      || seg.includes('@') || seg.includes('%40')
      || (/^[A-Za-z0-9_.~-]{20,}$/.test(seg) && /\d/.test(seg))
        ? ':id' : seg
    ))
    .join('/');
}

function enabled(): boolean {
  try {
    return localStorage.getItem(SWITCH_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Refresh the switch from the setting. Called once a session exists. */
export async function refreshAuthJournalSwitch(): Promise<void> {
  try {
    // @ts-ignore
    const { SettingsManager } = await import('../services/settings.service');
    const values = await SettingsManager.getMultipleGlobalSettings(
      [AUTH_JOURNAL_SETTING], { [AUTH_JOURNAL_SETTING]: false }, true,
    );
    const on = values?.[AUTH_JOURNAL_SETTING] === true || values?.[AUTH_JOURNAL_SETTING] === 'true';
    localStorage.setItem(SWITCH_KEY, on ? 'true' : 'false');
  } catch {
    /* keep the last known value */
  }
}

interface RecordInfo {
  reason?: string;
  user?: boolean;
  role?: boolean;
  detail?: AuthJournalEntry['detail'];
  withStack?: boolean;
}

/**
 * `info` may be a function so that building it runs inside the guard: a call
 * site that reads auth state must never be able to throw before the redirect
 * it is recording.
 */
export function recordAuthEvent(source: string, infoOrBuild: RecordInfo | (() => RecordInfo) = {}): void {
  if (!enabled()) return;
  try {
    const info = typeof infoOrBuild === 'function' ? infoOrBuild() : infoOrBuild;
    const entry: AuthJournalEntry = {
      at: new Date().toISOString(),
      source,
      path: typeof window !== 'undefined' ? window.location.pathname : '',
      visible: typeof document !== 'undefined' ? document.visibilityState === 'visible' : false,
    };
    if (info.reason !== undefined) entry.reason = info.reason;
    if (info.user !== undefined) entry.user = info.user;
    if (info.role !== undefined) entry.role = info.role;
    if (info.detail) entry.detail = info.detail;
    if (info.withStack !== false) entry.stack = frames();
    const entries = read();
    const last = entries[entries.length - 1];
    // A render-phase caller (ProtectedRoute) can run twice for one decision.
    if (last && last.source === entry.source && last.reason === entry.reason && last.path === entry.path
        && Date.parse(entry.at) - Date.parse(last.at) < DUPLICATE_WINDOW_MS) {
      return;
    }
    entries.push(entry);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch {
    /* a journal must never break the thing it observes */
  }
}

/**
 * Safety net for the paths nobody instrumented. On 2026-10-21 a background tab
 * went from /clerks-ecosystem to a full load of /firebase-login with no
 * recorded cause: something assigned window.location directly. Where the
 * Navigation API exists (Chromium; recent WebKit), its `navigate` event fires
 * synchronously inside a script-initiated navigation, so the stack recorded
 * there names the caller. Records every navigation TO the login, and every
 * reload. Absent API: nothing happens.
 */
/** Same-origin URLs keep their path, others only their origin; never a query. */
function describeUrl(raw: string): string {
  try {
    const url = new URL(raw, window.location.href);
    return url.origin === window.location.origin ? redactPath(url.pathname) : url.origin;
  } catch {
    return '';
  }
}

/**
 * Where this document came from. Safari gives the `navigate` event no stack,
 * so when a tab reloads into /firebase-login with nothing recorded before it
 * (2026-10-21, three times), the new document is the only witness: its
 * referrer, how it was reached, and how many HTTP redirects led here.
 */
export function recordBoot(): void {
  recordAuthEvent('boot', () => {
    const detail: AuthJournalEntry['detail'] = {
      from: typeof document !== 'undefined' && document.referrer ? describeUrl(document.referrer) : null,
    };
    try {
      const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (entry) {
        detail.navType = entry.type;
        detail.redirects = entry.redirectCount;
      }
    } catch {
      /* ignore */
    }
    return { withStack: false, detail };
  });
}

function watchNavigations(): void {
  const nav = (window as unknown as { navigation?: EventTarget }).navigation;
  if (!nav || typeof nav.addEventListener !== 'function') return;
  nav.addEventListener('navigate', (event: Event) => {
    try {
      const e = event as Event & {
        navigationType?: string;
        userInitiated?: boolean;
        destination?: { url?: string; sameDocument?: boolean };
      };
      const to = new URL(e.destination?.url || '', window.location.href);
      const toLogin = to.origin === window.location.origin && to.pathname.startsWith('/firebase-login');
      const leaves = e.destination?.sameDocument === false;
      if (!toLogin && !leaves && e.navigationType !== 'reload') return;
      const source = toLogin ? 'navigate-to-login' : e.navigationType === 'reload' ? 'navigate-reload' : 'navigate-away';
      recordAuthEvent(source, {
        reason: e.navigationType,
        detail: { to: describeUrl(to.href), userInitiated: !!e.userInitiated, sameDocument: !leaves },
      });
    } catch {
      /* ignore */
    }
  });
}

export function readAuthJournal(): AuthJournalEntry[] {
  return read();
}

export function clearAuthJournal(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

if (typeof window !== 'undefined') {
  try {
    Object.defineProperty(window, '__gridAuthJournal', {
      configurable: true,
      get: () => read(),
    });
    window.addEventListener('pagehide', () => recordAuthEvent('pagehide', { withStack: false }));
    watchNavigations();
  } catch {
    /* ignore */
  }
}
